import React, { useState } from 'react';
import Header from '~/components/Header';
import { ExpenseDetailTable } from './components/Table/ExpenseDetailTable';
import UserDateRange from '~/components/UserDateRange';
import { useSelector } from 'react-redux';
import { State } from '~/store';
import Skeleton from 'react-loading-skeleton';
import ScenarioDropdown from '~/components/ScenarioDropdown';
import SegmentedControl from '~/components/SegmentedControl';
import { ListBulletIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import ActualsAndOverridesLegend from '~/components/ActualsAndOverridesLegend/ActualsAndOverridesLegend';
import { FormulasProvider } from '~/components/Formulas/context/FormulasContext';
import { ExpenseFrequencyEnum, IFormulaTypeEnum } from '~/services/parallel/formulas.types';
import useQueryParams from '~/utils/hooks/useQueryParams';
import PullAccountingActuals from '~/components/PullAccountingActuals';
import { formulasApi } from '~/services/parallel/api/formulas/formulasApi';
import * as stringDate from '~/utils/stringDate';
import Checkbox from '~/components/Checkbox';
import Button from '~/components/Button';
import { ExpenseModal } from './components/ExpenseForm/ExpenseModal';
import { integrationsApi } from '~/services/parallel/api/integrationsApi';
import DeleteExpenseModal from './components/DeleteExpenseModal';
import DiscontinueExpenseModal from './components/DiscontinueExpenseModal';
import { Select } from '~/components/UncontrolledComponents/Select';
import { CATEGORY_OPTIONS, FREQUENCY_OPTIONS } from './components/ExpenseForm/formConstants';
import SearchInput from '~/components/Formulas/SearchInput/SearchInput';
import ExpenseFormulasTable from './ExpenseFormulasTable';
import ExpensesGraph from './components/Graph/ExpensesGraph';
import UnusedExpenseDatasources from './components/UnusedExpenseDatasources/UnusedExpenseDatasources';
import LinkDatasourceToExistingExpenseModal from './components/UnusedExpenseDatasources/LinkDatasourceToExistingExpenseModal';
import GenerateExpensesModal from './components/GenerateExpenses/GenerateExpensesModal';
import Typography from '~/components/Typography';
import expenseModelEmptyState from '~/assets/expenseModelEmptyState.svg';
import HoverPopover from '~/components/HoverPopover';

enum IViewEnum {
  MONTHLY = 'monthly',
  LIST = 'list',
}

interface ModalState {
  type: 'create' | 'generate' | 'edit' | 'duplicate' | 'delete' | 'discontinue' | 'link' | null;
  formulaUuid: string | null;
  datasourceUuid?: string | null;
}

const ExpensesPage = (): React.ReactNode => {
  const preferences = useSelector((state: State) => state.user.preferences);
  const { activeScenarioUuid } = useSelector((state: State) => state.scenario);
  const [modalState, setModalState] = useState<ModalState>({ type: null, formulaUuid: null });

  const { queryParams, setQueryParams } = useQueryParams();
  const selectedView = queryParams.get('view') ?? 'list';
  const selectedCategory = queryParams.get('category') ?? 'all';
  const selectedFrequency = queryParams.get('frequency') ?? 'all';
  const showPastExpenses = queryParams.get('showPastExpenses') === 'true';

  const { data: integrationsWithMappings } = integrationsApi.useGetIntegrationsWithMappingsQuery('expense');

  const { data: formulas, isLoading: formulasLoading } = formulasApi.useListFormulasQuery({
    startDate: preferences.defaultGraphStartDate,
    endDate: preferences.defaultGraphEndDate,
    scenarioUuid: activeScenarioUuid ?? undefined,
    types: [IFormulaTypeEnum.Expense],
    includes: ['calculations'],
  });

  if (formulasLoading) {
    return (
      <div className="w-full max-sm:min-h-screen max-sm:pb-32" data-testid="expenses-page-loading">
        <Header title="Expenses" />
        <div className="px-10 mt-10 mb-4">
          <Skeleton className="w-full h-[250px]" baseColor="#F8F9F6" />
        </div>
        <div className="px-10 max-w-full w-full">
          <Skeleton height={40} count={20} className="mb-4" baseColor="#F8F9F6" />
        </div>
      </div>
    );
  }

  if (!formulas) {
    throw new Error('No formulas');
  }

  const handleFilterChange = (key: string, value: string | boolean | null) => {
    const currentParams = Object.fromEntries(queryParams.entries());
    if (value) {
      currentParams[key] = String(value);
    } else {
      delete currentParams[key];
    }
    setQueryParams(currentParams);
  };

  const handleFilterChangeFor =
    (filterType: 'category' | 'frequency' | 'showPastExpenses') => (value: string | boolean | null) => {
      handleFilterChange(filterType, value);
    };

  const hasPastExpenses = formulas.some(
    (formula) =>
      (formula.recipe.endDate &&
        stringDate.isBefore({
          dateToCheck: formula.recipe.endDate,
          comparison: stringDate.getStringDate(),
        })) ||
      (formula.context?.expenseContext?.frequency === ExpenseFrequencyEnum.OneTime &&
        stringDate.isBefore({
          dateToCheck: formula.recipe.startDate as string,
          comparison: stringDate.getStringDate(),
        })),
  );

  return (
    <div className="w-full max-sm:min-h-screen pb-32">
      <Header
        title="Expenses"
        startChildren={
          <div className="flex flex-row gap-2">
            <ScenarioDropdown />
            {selectedView === IViewEnum.MONTHLY && <ActualsAndOverridesLegend />}
          </div>
        }
        endChildren={
          <div className="flex w-full justify-end items-center gap-4">
            <PullAccountingActuals />
            <UserDateRange pickerAlignment="right" />
          </div>
        }
      />
      <div>
        <FormulasProvider mode={IFormulaTypeEnum.Expense}>
          <div className="w-full flex items-center justify-between px-10 mt-10 mb-4">
            <div className="flex items-center gap-2 justify-center">
              <div className="w-[90px]">
                <SegmentedControl
                  name="view-control"
                  value={selectedView}
                  setValue={(value) => {
                    const currentParams = Object.fromEntries(queryParams.entries());
                    currentParams.view = value;
                    setQueryParams(currentParams);
                  }}
                  segments={[
                    {
                      value: IViewEnum.LIST,
                      label: <ListBulletIcon className="size-6 py-1" />,
                    },
                    {
                      value: IViewEnum.MONTHLY,
                      label: <TableCellsIcon className="size-6 py-1" />,
                    },
                  ]}
                />
              </div>
              {selectedView === IViewEnum.LIST && (
                <>
                  <div className="w-[200px] flex justify-center">
                    <Select
                      id="select-category"
                      value={selectedCategory}
                      onChange={handleFilterChangeFor('category')}
                      options={[{ label: 'All Categories', value: 'all' }, ...CATEGORY_OPTIONS]}
                      placeholder="All Categories"
                    />
                  </div>
                  <div className="w-[150px] flex justify-center">
                    <Select
                      id="select-frequency"
                      value={selectedFrequency}
                      onChange={handleFilterChangeFor('frequency')}
                      options={[{ label: 'All Frequencies', value: 'all' }, ...FREQUENCY_OPTIONS]}
                      placeholder="All Frequencies"
                    />
                  </div>
                </>
              )}
              {hasPastExpenses && (
                <div className="flex text-nowrap items-center gap-2 mb-1">
                  <Checkbox
                    checked={showPastExpenses}
                    onChange={handleFilterChangeFor('showPastExpenses')}
                    label="Show Past Expenses"
                    id="show-past-expenses"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <SearchInput />
              <Button
                fill="outline"
                onClick={() => {
                  setModalState({ type: 'create', formulaUuid: null });
                }}
                id="new-expense-button"
                className="max-sm:hidden !w-fit"
              >
                Add Expense
              </Button>
            </div>
          </div>
          {selectedView === IViewEnum.LIST && (
            <>
              <ExpensesGraph expenseFormulas={formulas} />
              {!!formulas.length && (
                <UnusedExpenseDatasources
                  expenseIntegrationsWithMappings={integrationsWithMappings ?? []}
                  onAction={(modalType, datasourceUuid) =>
                    setModalState({ type: modalType, formulaUuid: null, datasourceUuid })
                  }
                />
              )}
              {!!formulas.length ? (
                <ExpenseDetailTable
                  formulas={formulas}
                  formulasLoading={formulasLoading}
                  onEdit={(formulaUuid) => setModalState({ type: 'edit', formulaUuid })}
                  onDelete={(formulaUuid) => setModalState({ type: 'delete', formulaUuid })}
                  onDuplicate={(formulaUuid) => setModalState({ type: 'duplicate', formulaUuid })}
                  onDiscontinue={(formulaUuid) => setModalState({ type: 'discontinue', formulaUuid })}
                />
              ) : (
                <div className="flex flex-col items-center gap-2 mt-[18vh] pb-16">
                  <img src={expenseModelEmptyState} alt="Empty Tasks Illustration" className="w-64 h-auto" />
                  <div className="flex flex-col items-center gap-4 max-w-[438px] text-center">
                    <Typography color="empty" size="sm">
                      This will help you anticipate costs, manage cash flow, and make informed decisions on hiring and
                      growth — ensuring better resource allocation.
                    </Typography>
                    <div className="flex flex-col justify-center items-center gap-2">
                      <HoverPopover
                        buttonContent={
                          <Button
                            onClick={() => {
                              setModalState({ type: 'generate', formulaUuid: null });
                            }}
                            className="!w-fit !px-5"
                            disabled={integrationsWithMappings?.length === 0}
                          >
                            Generate Forecasted Expenses
                          </Button>
                        }
                        panelContent={
                          integrationsWithMappings?.length === 0 ? (
                            <div className="pb-1 bg-transparent">
                              <div className="flex flex-col bg-black px-5 py-3 rounded-lg !w-[242px]">
                                <Typography color="white" size="xs" weight="semibold">
                                  Required Data
                                </Typography>
                                <Typography color="white" size="xs">
                                  Accounting Integrations
                                </Typography>
                              </div>
                            </div>
                          ) : null
                        }
                        anchor="top"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {selectedView === IViewEnum.MONTHLY && (
            <div className="w-full pl-10">
              <ExpenseFormulasTable
                onEdit={(formulaUuid) => {
                  setModalState({ type: 'edit', formulaUuid });
                }}
              />
            </div>
          )}

          <DiscontinueExpenseModal
            isOpen={modalState.type === 'discontinue'}
            onClose={() => setModalState({ type: null, formulaUuid: null })}
            formulaUuid={modalState.formulaUuid ?? ''}
          />
          <DeleteExpenseModal
            isOpen={modalState.type === 'delete'}
            onClose={() => setModalState({ type: null, formulaUuid: null })}
            formulaUuid={modalState.formulaUuid ?? ''}
          />
          <ExpenseModal
            isOpen={['create', 'edit', 'duplicate'].includes(modalState.type ?? '')}
            isDuplicating={modalState.type === 'duplicate'}
            onClose={() => setModalState({ type: null, formulaUuid: null })}
            formulaUuid={modalState.formulaUuid ?? ''}
            scenarioUuid={activeScenarioUuid ?? null}
            formOverrides={{
              datasourceUuid: modalState.datasourceUuid ? [modalState.datasourceUuid] : null,
            }}
            integrationsWithMappings={integrationsWithMappings ?? []}
          />
          <LinkDatasourceToExistingExpenseModal
            isOpen={modalState.type === 'link'}
            onClose={() => setModalState({ type: null, formulaUuid: null, datasourceUuid: null })}
            datasourceUuid={modalState.datasourceUuid ?? ''}
            integrationsWithMappings={integrationsWithMappings ?? []}
          />
          <GenerateExpensesModal
            isOpen={modalState.type === 'generate'}
            onClose={() => setModalState({ type: null, formulaUuid: null })}
          />
        </FormulasProvider>
      </div>
    </div>
  );
};

const Page = (): React.ReactNode => {
  return <ExpensesPage />;
};

export default Page;
